import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Trash2, ChevronDown, Search, Wand2, Calendar, Hash, Users, EyeOff, Scissors,
} from 'lucide-react';


import type {
  ExportTemplateColumn,
  TemplateColumnSourceType,
  TransformType,
  CalculatedConfig,
  MarginRule,
  OtherTemplateRulesGroup,
} from './types';


import {
  SOURCE_TYPE_INFO,
  SOURCE_FIELD_OPTIONS,
  SOURCE_FIELD_GROUPS,
  TRANSFORM_TYPE_INFO,
  DATE_FORMAT_OPTIONS,
  SPLIT_DELIMITER_PRESETS,
  SPLIT_SELECT_JOIN_PRESETS,
} from './types';

import CalculatedColumnBuilder from './CalculatedColumnBuilder';
import AccountFilterPicker from './AccountFilterPicker';
import { ACCOUNT_SERIES_KEYS } from '@/lib/accountSeries';



// ─── Props ──────────────────────────────────────────────────────────

interface ColumnEditorExpandedProps {
  col: ExportTemplateColumn;
  columns: ExportTemplateColumn[];
  onLocalChange: (colId: string, updates: Partial<ExportTemplateColumn>) => void;
  onPersistChange: (colId: string, updates: Partial<ExportTemplateColumn>) => void;
  onDelete: (colId: string) => void;
  saving: boolean;
  isLocked: boolean;
  marginRules: MarginRule[];
  /** Margin rules from other templates, grouped by template */
  otherTemplateRulesGroups?: OtherTemplateRulesGroup[];
  /** Optional: use wider layout for pop-out */
  wide?: boolean;
  /** Export's Date To filter (YYYY-MM-DD), used to preview date columns in "Last Day of Export" mode */
  rangeEndDate?: string;
}

const ColumnEditorExpanded: React.FC<ColumnEditorExpandedProps> = ({
  col,
  columns,
  onLocalChange,
  onPersistChange,
  onDelete,
  saving,
  isLocked,
  marginRules,
  otherTemplateRulesGroups = [],
  wide = false,
  rangeEndDate,
}) => {

  const [fieldPickerOpen, setFieldPickerOpen] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');

  const filteredFields = SOURCE_FIELD_OPTIONS.filter(f =>
    !fieldSearch || f.label.toLowerCase().includes(fieldSearch.toLowerCase()) || f.field.toLowerCase().includes(fieldSearch.toLowerCase())
  );

  const getFieldLabel = (fieldName: string | null) => {
    if (!fieldName) return null;
    return SOURCE_FIELD_OPTIONS.find(f => f.field === fieldName)?.label || fieldName;
  };

  if (isLocked) {
    return (
      <div className="px-3 pb-3 pt-1 border-t border-border/40 text-[10px] text-muted-foreground/60 space-y-1">
        <div><span className="text-muted-foreground">Source:</span> <span className="text-foreground">{SOURCE_TYPE_INFO[col.source_type as TemplateColumnSourceType]?.label || col.source_type}</span></div>
        {col.source_field && <div><span className="text-muted-foreground">Field:</span> <span className="text-foreground">{getFieldLabel(col.source_field)}</span></div>}
        {col.static_value && <div><span className="text-muted-foreground">Value:</span> <span className="text-foreground">{col.static_value}</span></div>}
        {col.notes && <div><span className="text-muted-foreground">Notes:</span> <span className="text-foreground">{col.notes}</span></div>}
      </div>
    );
  }

  return (
    <div className={`px-3 pb-3 pt-1 border-t border-border/40 space-y-2 ${wide ? 'px-4' : ''}`}>
      {/* Export Column Name */}
      <div className={wide ? 'grid grid-cols-2 gap-3' : ''}>
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase">Export Column Name</Label>
          <Input value={col.export_column_name}
            onChange={e => onLocalChange(col.id, { export_column_name: e.target.value })}
            onBlur={() => onPersistChange(col.id, { export_column_name: col.export_column_name })}
            className="dark-input text-xs h-7" />
        </div>

        {/* Source Type */}
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase">Source Type</Label>
          <Select value={col.source_type} onValueChange={(v: string) => {
            const st = v as TemplateColumnSourceType;
            const prevExclude = col.transform_config?.excludeFromExport;
            const updates: Partial<ExportTemplateColumn> = { source_type: st };
            if (st !== 'source_field') { updates.source_field = null; updates.transform_type = null; }
            if (st !== 'static_value') updates.static_value = null;
            if (st !== 'manual_default') updates.default_value = null;
            if (st === 'calculated') { updates.transform_config = { calcType: 'concat', parts: [], ...(prevExclude ? { excludeFromExport: true } : {}) }; }
            else if (st === 'date') { updates.transform_config = { dateMode: 'today', dateFormat: 'DD-MM-YYYY', ...(prevExclude ? { excludeFromExport: true } : {}) }; }
            else if (st !== 'source_field') { updates.transform_config = prevExclude ? { excludeFromExport: true } : null; }
            onLocalChange(col.id, updates);
            onPersistChange(col.id, updates);
          }}>

            <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(Object.keys(SOURCE_TYPE_INFO) as TemplateColumnSourceType[]).map(st => (
                <SelectItem key={st} value={st}>
                  <span className="flex items-center gap-2">
                    <Badge className={`${SOURCE_TYPE_INFO[st].color} text-[9px] px-1 py-0`}>{SOURCE_TYPE_INFO[st].label}</Badge>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[9px] text-muted-foreground/60 mt-0.5">{SOURCE_TYPE_INFO[col.source_type as TemplateColumnSourceType]?.description}</p>
        </div>
      </div>

      {/* Source Field Picker */}
      {col.source_type === 'source_field' && (
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase">Source Field</Label>
          <Popover open={fieldPickerOpen} onOpenChange={(open) => { setFieldPickerOpen(open); setFieldSearch(''); }}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-between dark-input text-xs h-7 font-normal">
                {col.source_field ? (
                  <span className="flex items-center gap-1.5">
                    <span className="text-foreground">{getFieldLabel(col.source_field)}</span>
                    <span className="text-[9px] text-muted-foreground/60">{col.source_field}</span>
                  </span>
                ) : <span className="text-muted-foreground">Select a field...</span>}
                <ChevronDown className="w-3 h-3 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0 max-h-[350px] overflow-hidden" align="start">
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={fieldSearch} onChange={e => setFieldSearch(e.target.value)} placeholder="Search fields..." className="dark-input text-xs h-7 pl-7" autoFocus />
                </div>
              </div>
              <div className="overflow-y-auto max-h-[290px] p-1">
                {SOURCE_FIELD_GROUPS.map(group => {
                  const groupFields = filteredFields.filter(f => f.group === group);
                  if (groupFields.length === 0) return null;
                  return (
                    <div key={group} className="mb-1">
                      <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1">{group}</p>
                      {groupFields.map(f => (
                        <button key={f.field}
                          className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-secondary/60 transition-colors flex items-center justify-between ${col.source_field === f.field ? 'bg-primary/10 text-primary' : 'text-foreground'}`}
                          onClick={() => {
                            onLocalChange(col.id, { source_field: f.field });
                            onPersistChange(col.id, { source_field: f.field });
                            setFieldPickerOpen(false);
                          }}>
                          <span>{f.label}</span>
                          <span className="text-[9px] text-muted-foreground/50">{f.field}</span>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* ─── Account ID Series + Account Picker (when Account ID is selected) ─── */}
      {col.source_type === 'source_field' && col.source_field === 'account_id' && (() => {
        const colSeries: string[] = col.transform_config?.selectedSeries || [];
        const colAccountIds: string[] = col.transform_config?.selectedAccountIds || [];

        const updateAccountConfig = (updates: Record<string, any>) => {
          const newConfig = { ...(col.transform_config || {}), ...updates };
          onLocalChange(col.id, { transform_config: newConfig });
          onPersistChange(col.id, { transform_config: newConfig });
        };

        const toggleSeries = (s: string) => {
          const next = colSeries.includes(s)
            ? colSeries.filter(x => x !== s)
            : [...colSeries, s];
          // When series changes, clear account selections that no longer match
          const filteredIds = next.length > 0
            ? colAccountIds.filter(id => next.includes(id.charAt(0)))
            : colAccountIds;
          updateAccountConfig({ selectedSeries: next, selectedAccountIds: filteredIds });
        };

        return (
          <div className="border-t border-teal-700/20 pt-2 mt-1 space-y-2">
            <div className="flex items-center gap-1.5 mb-1">
              <Users className="w-3 h-3 text-teal-400" />
              <Label className="text-[10px] text-teal-400 uppercase font-semibold">Filter by Specific Accounts</Label>
            </div>

            {/* Series Selector */}
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase">Select Series</Label>
              <div className="flex gap-1 mt-1">
                {ACCOUNT_SERIES_KEYS.map(s => (
                  <button
                    key={s}
                    onClick={() => toggleSeries(s)}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                      colSeries.includes(s)
                        ? 'bg-teal-900/30 text-teal-400 border-teal-700/50'
                        : 'bg-secondary/40 text-muted-foreground border-border hover:border-teal-700/30'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {colSeries.length === 0 && (
                <p className="text-[9px] text-muted-foreground/50 mt-1">Select a series to see accounts</p>
              )}
            </div>

            {/* Account Picker (only shown when at least one series is selected) */}
            {colSeries.length > 0 && (
              <AccountFilterPicker
                seriesFilter={colSeries}
                selectedAccountIds={colAccountIds}
                onSelectionChange={(ids) => {
                  updateAccountConfig({ selectedAccountIds: ids });
                }}
                maxHeight="max-h-[180px]"
                compact
              />
            )}
          </div>
        );
      })()}

      {/* Static Value */}

      {col.source_type === 'static_value' && (
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase">Static Value</Label>
          <Input value={col.static_value || ''}
            onChange={e => onLocalChange(col.id, { static_value: e.target.value })}
            onBlur={() => onPersistChange(col.id, { static_value: col.static_value })}
            placeholder="Enter value..." className="dark-input text-xs h-7" />
        </div>
      )}

      {/* Manual Default Value */}
      {col.source_type === 'manual_default' && (
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase">Default Value</Label>
          <Input value={col.default_value || ''}
            onChange={e => onLocalChange(col.id, { default_value: e.target.value })}
            onBlur={() => onPersistChange(col.id, { default_value: col.default_value })}
            placeholder="Enter default..." className="dark-input text-xs h-7" />
          <p className="text-[9px] text-muted-foreground/60 mt-0.5">This value can be overridden per export run.</p>
        </div>
      )}

      {/* ─── Blank Column Invoice Number Editor ─── */}
      {col.source_type === 'blank' && (() => {
        const blankConfig = col.transform_config || { blankMode: 'empty' };
        const blankMode = blankConfig.blankMode || 'empty';
        const startingNum = blankConfig.startingInvoiceNumber || 10001;

        const updateBlankConfig = (updates: Record<string, any>) => {
          const newConfig = { ...blankConfig, ...updates };
          onLocalChange(col.id, { transform_config: newConfig });
          onPersistChange(col.id, { transform_config: newConfig });
        };

        return (
          <div className="border-t border-border/30 pt-2 mt-1 space-y-2">
            <div className="flex items-center gap-1.5 mb-1">
              <Hash className="w-3 h-3 text-indigo-400" />
              <Label className="text-[10px] text-indigo-400 uppercase font-semibold">Blank Column Mode</Label>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                className={`px-2 py-1.5 rounded border text-[10px] text-left transition-colors ${blankMode === 'empty' ? 'border-gray-500/50 bg-gray-900/30 text-gray-300' : 'border-border/60 text-muted-foreground hover:border-border'}`}
                onClick={() => updateBlankConfig({ blankMode: 'empty' })}
              >
                <div className="font-medium">Empty</div>
                <div className="text-[9px] opacity-60 mt-0.5">Intentionally blank</div>
              </button>
              <button
                className={`px-2 py-1.5 rounded border text-[10px] text-left transition-colors ${blankMode === 'invoice_number' ? 'border-indigo-500/50 bg-indigo-900/30 text-indigo-300' : 'border-border/60 text-muted-foreground hover:border-border'}`}
                onClick={() => updateBlankConfig({ blankMode: 'invoice_number', startingInvoiceNumber: startingNum })}
              >
                <div className="font-medium">Invoice Number</div>
                <div className="text-[9px] opacity-60 mt-0.5">Auto-increment from start</div>
              </button>
            </div>
            {blankMode === 'invoice_number' && (
              <div className="space-y-1.5">
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Starting Invoice Number</Label>
                  <Input
                    type="number"
                    value={startingNum}
                    onChange={e => updateBlankConfig({ startingInvoiceNumber: parseInt(e.target.value) || 0 })}
                    placeholder="e.g. 10001"
                    className="dark-input text-xs h-7"
                    min={1}
                  />
                </div>
                <div className="flex items-start gap-1.5 text-[9px] text-indigo-400/70 bg-indigo-900/10 border border-indigo-700/20 rounded px-2 py-1.5">
                  <Hash className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span>Invoice numbers follow the grouping rules from Invoice Run Settings. Assign invoices in the Invoice Run panel before exporting.</span>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Calculated Column Builder */}
      {col.source_type === 'calculated' && (
        <CalculatedColumnBuilder
          config={col.transform_config as CalculatedConfig | null}
          onChange={(newConfig) => {
            onLocalChange(col.id, { transform_config: newConfig });
            onPersistChange(col.id, { transform_config: newConfig });
          }}
          marginRules={marginRules}
          otherTemplateRulesGroups={otherTemplateRulesGroups}
          templateColumns={columns}
          currentColumnId={col.id}
        />
      )}


      {/* ─── Date Column Editor ─── */}
      {col.source_type === 'date' && (() => {
        const dateConfig = col.transform_config || { dateMode: 'today', dateFormat: 'DD-MM-YYYY' };
        const dateMode = dateConfig.dateMode || 'today';
        const dateFormat = dateConfig.dateFormat || 'DD-MM-YYYY';
        const customDate = dateConfig.customDate || '';

        // Live preview
        const now = new Date();
        const todayIso = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
        const previewIso = dateMode === 'custom' && customDate ? customDate
          : dateMode === 'range_end' && rangeEndDate ? rangeEndDate
          : todayIso;
        const [pY, pM, pD] = previewIso.split('-');
        const MONTH_NAMES_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        let previewFormatted = previewIso;
        if (pY && pM && pD) {
          switch (dateFormat) {
            case 'DD/MM/YYYY': previewFormatted = `${pD}/${pM}/${pY}`; break;
            case 'D/M/YYYY': previewFormatted = `${parseInt(pD)}/${parseInt(pM)}/${pY}`; break;
            case 'YYYY-MM-DD': previewFormatted = previewIso; break;
            case 'MM/DD/YYYY': previewFormatted = `${pM}/${pD}/${pY}`; break;
            case 'DD-MM-YYYY': previewFormatted = `${pD}-${pM}-${pY}`; break;
            case 'D MMM YYYY': previewFormatted = `${parseInt(pD)} ${MONTH_NAMES_SHORT[parseInt(pM)-1] || pM} ${pY}`; break;
          }
        }

        const updateDateConfig = (updates: Record<string, any>) => {
          const newConfig = { ...dateConfig, ...updates };
          onLocalChange(col.id, { transform_config: newConfig });
          onPersistChange(col.id, { transform_config: newConfig });
        };

        return (
          <div className="border-t border-indigo-700/20 pt-2 mt-1 space-y-2">
            <div className="flex items-center gap-1.5 mb-1">
              <Calendar className="w-3 h-3 text-indigo-400" />
              <Label className="text-[10px] text-indigo-400 uppercase font-semibold">Date Settings</Label>
            </div>

            {/* Date Mode */}
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase">Date Mode</Label>
              <div className="grid grid-cols-3 gap-1.5 mt-1">
                <button
                  className={`px-2 py-1.5 rounded border text-[10px] text-left transition-colors ${dateMode === 'today' ? 'border-indigo-500/50 bg-indigo-900/30 text-indigo-300' : 'border-border/60 text-muted-foreground hover:border-border'}`}
                  onClick={() => updateDateConfig({ dateMode: 'today' })}
                >
                  <div className="font-medium">Today&apos;s Date</div>
                  <div className="text-[9px] opacity-60 mt-0.5">Auto-fills at export time</div>
                </button>
                <button
                  className={`px-2 py-1.5 rounded border text-[10px] text-left transition-colors ${dateMode === 'custom' ? 'border-indigo-500/50 bg-indigo-900/30 text-indigo-300' : 'border-border/60 text-muted-foreground hover:border-border'}`}
                  onClick={() => updateDateConfig({ dateMode: 'custom' })}
                >
                  <div className="font-medium">Custom Date</div>
                  <div className="text-[9px] opacity-60 mt-0.5">Fixed date every export</div>
                </button>
                <button
                  className={`px-2 py-1.5 rounded border text-[10px] text-left transition-colors ${dateMode === 'range_end' ? 'border-indigo-500/50 bg-indigo-900/30 text-indigo-300' : 'border-border/60 text-muted-foreground hover:border-border'}`}
                  onClick={() => updateDateConfig({ dateMode: 'range_end' })}
                >
                  <div className="font-medium">Last Day of Export</div>
                  <div className="text-[9px] opacity-60 mt-0.5">Uses the Date To filter</div>
                </button>
              </div>
            </div>

            {/* Custom Date Input */}
            {dateMode === 'custom' && (
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase">Custom Date</Label>
                <Input
                  type="date"
                  value={customDate}
                  onChange={e => updateDateConfig({ customDate: e.target.value })}
                  className="dark-input text-xs h-7"
                />
              </div>
            )}

            {/* Date Format */}
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase">Date Format</Label>
              <Select value={dateFormat} onValueChange={(v) => updateDateConfig({ dateFormat: v })}>
                <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DATE_FORMAT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-2">
                        <span>{opt.label}</span>
                        <span className="text-[9px] text-muted-foreground/60">e.g. {opt.example}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Live Preview */}
            <div className="bg-indigo-900/15 border border-indigo-700/25 rounded px-2.5 py-2">
              <div className="flex items-center justify-between">
                <span className="text-[9px] text-indigo-400/70 uppercase">Preview</span>
                <span className="text-[9px] text-muted-foreground/50">
                  {dateMode === 'today' ? "Today's date"
                    : dateMode === 'range_end' ? (rangeEndDate ? "Export's Date To" : "No Date To set — today's date")
                    : 'Custom date'}
                </span>
              </div>
              <div className="text-sm font-mono text-indigo-300 mt-1">{previewFormatted}</div>
            </div>
          </div>
        );
      })()}

      {/* ─── Transform Section ─── */}
      {col.source_type === 'source_field' && (
        <div className="border-t border-border/30 pt-2 mt-2">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Wand2 className="w-3 h-3 text-cyan-400" />
            <Label className="text-[10px] text-muted-foreground uppercase">Transform</Label>
          </div>
          <Select
            value={col.transform_type || 'none'}
            onValueChange={(v: string) => {
              const tt = v as TransformType;
              // Preserve account filter keys when changing transform type
              const existingAccountKeys: Record<string, any> = {};
              if (col.transform_config?.selectedSeries) existingAccountKeys.selectedSeries = col.transform_config.selectedSeries;
              if (col.transform_config?.selectedAccountIds) existingAccountKeys.selectedAccountIds = col.transform_config.selectedAccountIds;

              let newConfig: any;
              if (tt === 'date_format') {
                newConfig = { format: 'DD/MM/YYYY', ...existingAccountKeys };
              } else if (tt === 'split_select') {
                newConfig = {
                  splitSelectDelimiter: '/',
                  splitSelectParts: [],
                  splitSelectAll: true,
                  splitSelectJoinWith: '/',
                  splitSelectTrimParts: true,
                  ...existingAccountKeys,
                };
              } else if (Object.keys(existingAccountKeys).length > 0) {
                newConfig = existingAccountKeys;
              } else {
                newConfig = null;
              }

              const updates: Partial<ExportTemplateColumn> = {
                transform_type: tt === 'none' ? null : tt,
                transform_config: newConfig,
              };
              onLocalChange(col.id, updates);
              onPersistChange(col.id, updates);
            }}

          >
            <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(Object.keys(TRANSFORM_TYPE_INFO) as TransformType[]).map(tt => (
                <SelectItem key={tt} value={tt}>
                  <span className="flex items-center gap-2">
                    <Badge className={`${TRANSFORM_TYPE_INFO[tt].color} text-[9px] px-1 py-0`}>{TRANSFORM_TYPE_INFO[tt].label}</Badge>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[9px] text-muted-foreground/60 mt-0.5">
            {TRANSFORM_TYPE_INFO[(col.transform_type || 'none') as TransformType]?.description || ''}
          </p>

          {/* Date Format Config */}
          {col.transform_type === 'date_format' && (
            <div className="mt-1.5">
              <Label className="text-[10px] text-muted-foreground uppercase">Date Format</Label>
              <Select
                value={col.transform_config?.format || 'DD/MM/YYYY'}
                onValueChange={(v) => {
                  // Preserve account filter keys
                  const existingAccountKeys: Record<string, any> = {};
                  if (col.transform_config?.selectedSeries) existingAccountKeys.selectedSeries = col.transform_config.selectedSeries;
                  if (col.transform_config?.selectedAccountIds) existingAccountKeys.selectedAccountIds = col.transform_config.selectedAccountIds;
                  const config = { format: v, ...existingAccountKeys };
                  onLocalChange(col.id, { transform_config: config });
                  onPersistChange(col.id, { transform_config: config });
                }}
              >
                <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DATE_FORMAT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-2">
                        <span>{opt.label}</span>
                        <span className="text-[9px] text-muted-foreground/60">e.g. {opt.example}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Clean Address Info */}
          {col.transform_type === 'clean_address' && (
            <div className="mt-1.5 flex items-start gap-1.5 text-[9px] text-cyan-400/70 bg-cyan-900/10 border border-cyan-700/20 rounded px-2 py-1">
              <Wand2 className="w-3 h-3 flex-shrink-0 mt-0.5" />
              <span>Applies alias rules first, then removes parentheses, trims after comma, shortens street suffixes, and conservatively removes house numbers.</span>
            </div>
           )}

          {/* ─── Split & Select Config ─── */}
          {col.transform_type === 'split_select' && (() => {
            const cfg = col.transform_config || {};
            const delimiter: string = cfg.splitSelectDelimiter || '/';
            const selectAll: boolean = cfg.splitSelectAll ?? true;
            const selectedParts: number[] = cfg.splitSelectParts || [];
            const joinWith: string = cfg.splitSelectJoinWith ?? '/';
            const trimParts: boolean = cfg.splitSelectTrimParts !== false;

            // Preserve account filter keys
            const preserveKeys = () => {
              const keys: Record<string, any> = {};
              if (cfg.selectedSeries) keys.selectedSeries = cfg.selectedSeries;
              if (cfg.selectedAccountIds) keys.selectedAccountIds = cfg.selectedAccountIds;
              if (cfg.excludeFromExport) keys.excludeFromExport = cfg.excludeFromExport;
              return keys;
            };

            const updateSplitConfig = (updates: Record<string, any>) => {
              const newConfig = { ...cfg, ...updates };
              onLocalChange(col.id, { transform_config: newConfig });
              onPersistChange(col.id, { transform_config: newConfig });
            };

            const togglePart = (idx: number) => {
              const next = selectedParts.includes(idx)
                ? selectedParts.filter(p => p !== idx)
                : [...selectedParts, idx].sort((a, b) => a - b);
              updateSplitConfig({ splitSelectParts: next, splitSelectAll: false });
            };

            // Example preview
            const exampleValue = 'Value1 / Value2 / Value3';
            const previewParts = exampleValue.split(delimiter);
            let previewResult = '';
            if (selectAll) {
              previewResult = previewParts.map(p => trimParts ? p.trim() : p).filter(p => p !== '').join(joinWith);
            } else if (selectedParts.length > 0) {
              previewResult = selectedParts
                .sort((a, b) => a - b)
                .map(idx => idx >= 0 && idx < previewParts.length ? (trimParts ? previewParts[idx].trim() : previewParts[idx]) : '')
                .filter(p => p !== '')
                .join(joinWith);
            } else {
              previewResult = exampleValue;
            }

            // Max parts to show (up to 10)
            const MAX_PARTS = 10;

            return (
              <div className="mt-2 border-t border-sky-700/20 pt-2 space-y-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <Scissors className="w-3 h-3 text-sky-400" />
                  <Label className="text-[10px] text-sky-400 uppercase font-semibold">Split & Select Settings</Label>
                </div>

                {/* Delimiter Picker */}
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Split Delimiter</Label>
                  <Select value={delimiter} onValueChange={(v) => updateSplitConfig({ splitSelectDelimiter: v })}>
                    <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SPLIT_DELIMITER_PRESETS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className="font-mono">{opt.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[9px] text-muted-foreground/50 mt-0.5">The character(s) to split the value by</p>
                </div>

                {/* Part Selection */}
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Parts to Include</Label>
                  <div className="mt-1 space-y-1">
                    {/* Select All toggle */}
                    <label className="flex items-center gap-2 cursor-pointer px-1.5 py-1 rounded hover:bg-sky-900/10 transition-colors">
                      <Checkbox
                        checked={selectAll}
                        onCheckedChange={(v) => updateSplitConfig({ splitSelectAll: !!v, splitSelectParts: [] })}
                        className="h-3.5 w-3.5"
                      />
                      <span className="text-[10px] font-medium text-sky-400">All Parts</span>
                      <span className="text-[9px] text-muted-foreground/50 ml-auto">Include every value after splitting</span>
                    </label>

                    {!selectAll && (
                      <div className="border border-sky-700/20 rounded p-1.5 bg-sky-900/5">
                        <p className="text-[9px] text-muted-foreground/60 mb-1">Select which parts to keep (1-indexed positions):</p>
                        <div className="flex flex-wrap gap-1">
                          {Array.from({ length: MAX_PARTS }, (_, i) => (
                            <button
                              key={i}
                              onClick={() => togglePart(i)}
                              className={`min-w-[28px] px-1.5 py-1 rounded text-[10px] font-medium border transition-colors ${
                                selectedParts.includes(i)
                                  ? 'bg-sky-900/40 text-sky-300 border-sky-600/50'
                                  : 'bg-secondary/30 text-muted-foreground border-border/50 hover:border-sky-700/30 hover:text-sky-400'
                              }`}
                            >
                              {i + 1}
                            </button>
                          ))}
                        </div>
                        {selectedParts.length > 0 && (
                          <p className="text-[9px] text-sky-400/70 mt-1.5">
                            Keeping part{selectedParts.length > 1 ? 's' : ''}: {selectedParts.map(p => p + 1).join(', ')}
                          </p>
                        )}
                        {selectedParts.length === 0 && (
                          <p className="text-[9px] text-amber-400/70 mt-1.5">
                            No parts selected — click a number to select which parts to keep
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Join Separator */}
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Join Separator</Label>
                  <Select value={joinWith} onValueChange={(v) => updateSplitConfig({ splitSelectJoinWith: v })}>
                    <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SPLIT_SELECT_JOIN_PRESETS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className="font-mono">{opt.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[9px] text-muted-foreground/50 mt-0.5">How selected parts are joined back together</p>
                </div>

                {/* Trim Parts Toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={trimParts}
                    onCheckedChange={(v) => updateSplitConfig({ splitSelectTrimParts: v })}
                    className="scale-75"
                  />
                  <span className="text-[10px] text-muted-foreground">Trim whitespace from each part</span>
                </label>

                {/* Live Preview */}
                <div className="bg-sky-900/15 border border-sky-700/25 rounded px-2.5 py-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] text-sky-400/70 uppercase">Preview</span>
                    <span className="text-[9px] text-muted-foreground/50">
                      {selectAll ? 'All parts' : `Parts: ${selectedParts.length > 0 ? selectedParts.map(p => p + 1).join(', ') : 'none'}`}
                    </span>
                  </div>
                  <div className="text-[10px] text-muted-foreground/60 mt-0.5 font-mono">Input: &quot;{exampleValue}&quot;</div>
                  <div className="text-sm font-mono text-sky-300 mt-1">{previewResult || '(empty)'}</div>
                </div>
              </div>
            );
          })()}
        </div>
      )}




      {/* Options Row */}
      <div className="flex items-center gap-4 pt-1 flex-wrap">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Switch checked={col.is_enabled} onCheckedChange={(v) => {
            onLocalChange(col.id, { is_enabled: v });
            onPersistChange(col.id, { is_enabled: v });
          }} className="scale-75" />
          <span className="text-[10px] text-muted-foreground">Enabled</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Switch checked={col.is_required} onCheckedChange={(v) => {
            onLocalChange(col.id, { is_required: v });
            onPersistChange(col.id, { is_required: v });
          }} className="scale-75" />
          <span className="text-[10px] text-muted-foreground">Required</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Switch
            checked={!!col.transform_config?.excludeFromExport}
            onCheckedChange={(v) => {
              const newConfig = { ...(col.transform_config || {}), excludeFromExport: v };
              onLocalChange(col.id, { transform_config: newConfig });
              onPersistChange(col.id, { transform_config: newConfig });
            }}
            className="scale-75"
          />
          <span className={`text-[10px] flex items-center gap-1 ${col.transform_config?.excludeFromExport ? 'text-orange-400' : 'text-muted-foreground'}`}>
            <EyeOff className="w-3 h-3" />
            Exclude from Export
          </span>
        </label>
      </div>

      {/* Exclude from Export info */}
      {col.transform_config?.excludeFromExport && (
        <div className="flex items-start gap-1.5 text-[9px] text-orange-400/80 bg-orange-900/10 border border-orange-700/20 rounded px-2 py-1.5">
          <EyeOff className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>This column is used as a filter/criteria only and will <strong>not</strong> appear in the exported output file.</span>
        </div>
      )}


      {/* Notes */}
      <div>
        <Label className="text-[10px] text-muted-foreground uppercase">Notes</Label>
        <Input value={col.notes || ''}
          onChange={e => onLocalChange(col.id, { notes: e.target.value })}
          onBlur={() => onPersistChange(col.id, { notes: col.notes })}
          placeholder="Optional notes..." className="dark-input text-xs h-7" />
      </div>

      {/* Delete */}
      <div className="flex justify-end pt-1">
        <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20"
          onClick={() => onDelete(col.id)} disabled={saving}>
          <Trash2 className="w-3 h-3 mr-1" /> Remove Column
        </Button>
      </div>
    </div>
  );
};

export default ColumnEditorExpanded;
